import { useState } from 'react';
import '../styles/ShareModal.css';
import { soundManager } from '../utils/sounds';
import { recordShare } from '../utils/achievements';

/**
 * Share / Challenge modal shown after puzzle completion.
 * Props:
 *   stage    – completed stage object
 *   timeLeft – seconds remaining when puzzle was solved
 *   onClose  – close handler
 *   onAchievementUnlock – callback(newIds[]) for any achievements triggered by sharing
 */
function ShareModal({ stage, timeLeft, onClose, onAchievementUnlock }) {
  const [copied, setCopied] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);

  // Plain share link – just opens the puzzle
  const shareUrl = `${window.location.origin}${window.location.pathname}?share=${stage.id}`;

  // Challenge link – includes a par time to beat (slightly harder than your own time)
  const parTime = Math.max(5, Math.floor(timeLeft * 0.85));
  const challengeUrl = `${window.location.origin}${window.location.pathname}?challenge=${stage.id}&par=${parTime}`;

  const copyLink = async (url, setCopiedState) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  const handleShareCopy = () => {
    soundManager.playClick();
    copyLink(shareUrl, setCopied);
    const newIds = recordShare();
    if (newIds.length > 0) onAchievementUnlock?.(newIds);
  };

  const handleChallengeCopy = () => {
    soundManager.playClick();
    copyLink(challengeUrl, setChallengeCopied);
    const newIds = recordShare();
    if (newIds.length > 0) onAchievementUnlock?.(newIds);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `${s}s`;
  };

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <button className="share-close" onClick={() => { soundManager.playClick(); onClose(); }}>×</button>

        <div className="share-header">
          <span className="share-char">{stage.chinese}</span>
          <div>
            <h2 className="share-title">Share Puzzle</h2>
            <p className="share-subtitle">{stage.english} · {stage.mode === 'easy' ? 'Basic' : 'Advanced'}</p>
          </div>
        </div>

        {/* Share link */}
        <div className="share-section">
          <h3 className="share-section-title">🔗 Share Link</h3>
          <p className="share-section-desc">Send this link so friends can try the same puzzle.</p>
          <div className="share-link-row">
            <input
              className="share-link-input"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
            />
            <button className={`share-copy-btn ${copied ? 'copied' : ''}`} onClick={handleShareCopy}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Challenge link */}
        <div className="share-section">
          <h3 className="share-section-title">⚔️ Challenge a Friend</h3>
          <p className="share-section-desc">
            Challenge them to beat your par time of{' '}
            <strong>{formatTime(parTime)}</strong> remaining!
          </p>
          <div className="share-link-row">
            <input
              className="share-link-input"
              readOnly
              value={challengeUrl}
              onFocus={(e) => e.target.select()}
            />
            <button
              className={`share-copy-btn challenge ${challengeCopied ? 'copied' : ''}`}
              onClick={handleChallengeCopy}
            >
              {challengeCopied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Native share */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            className="share-native-btn"
            onClick={() => {
              soundManager.playClick();
              navigator.share({
                title: `WriteQuest – Can you solve ${stage.chinese} (${stage.english})?`,
                text: `I just solved the ${stage.english} puzzle in WriteQuest! Try to beat my par time of ${formatTime(parTime)}.`,
                url: challengeUrl,
              }).catch(() => {});
              const newIds = recordShare();
              if (newIds.length > 0) onAchievementUnlock?.(newIds);
            }}
          >
            📤 Share via…
          </button>
        )}
      </div>
    </div>
  );
}

export default ShareModal;
